import {
  defaultImageAdjustments,
  normalizeImagePaint,
  type ImageAdjustments,
  type ImagePaint,
} from "@/document/model/styles";
import type { Rect } from "@/lib/math/rect";
import type { SvgImportIssueCollector } from "./issues";
import { childElements, elementLocalName, readSvgLength } from "./parseSvgDocument";
import type { SvgTransformMatrix } from "./svgTransforms";

export function readSvgImageHref(element: Element): string | undefined {
  const href = element.getAttribute("href")
    ?? element.getAttribute("xlink:href")
    ?? element.getAttributeNS("http://www.w3.org/1999/xlink", "href");
  return href?.trim() || undefined;
}

export function parseCurvaImagePaintAttribute(
  raw: string | null | undefined,
  issues: SvgImportIssueCollector,
): Partial<ImagePaint> | undefined {
  if (!raw?.trim()) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<ImagePaint>;
    if (!parsed || typeof parsed !== "object") {
      return undefined;
    }
    return parsed;
  } catch {
    issues.addOnce("unsupported-attribute", "Could not parse data-curva-image-paint metadata", "data-curva-image-paint");
    return undefined;
  }
}

export function parseCurvaImageAdjustmentsAttribute(
  raw: string | null | undefined,
  issues: SvgImportIssueCollector,
): ImageAdjustments | undefined {
  if (!raw?.trim()) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(raw) as ImageAdjustments;
    return { ...defaultImageAdjustments(), ...parsed };
  } catch {
    issues.addOnce("unsupported-attribute", "Could not parse data-curva-image-adjustments metadata", "data-curva-image-adjustments");
    return undefined;
  }
}

export function imagePaintFromImageElement(
  element: Element,
  issues: SvgImportIssueCollector,
): ImagePaint | undefined {
  const source = readSvgImageHref(element);
  if (!source) {
    issues.addOnce("unsupported-image", "Image element is missing href", elementLocalName(element));
    return undefined;
  }

  const intrinsicWidth = Math.max(1, readSvgLength(element.getAttribute("width"), 1));
  const intrinsicHeight = Math.max(1, readSvgLength(element.getAttribute("height"), 1));
  const metadata = parseCurvaImagePaintAttribute(element.getAttribute("data-curva-image-paint"), issues);
  const adjustments = parseCurvaImageAdjustmentsAttribute(element.getAttribute("data-curva-image-adjustments"), issues);

  return normalizeImagePaint({
    type: "image",
    source,
    intrinsicWidth,
    intrinsicHeight,
    fillMode: metadata?.fillMode ?? "fill",
    crop: metadata?.crop ?? { x: 0, y: 0, width: 1, height: 1 },
    flipX: metadata?.flipX ?? false,
    flipY: metadata?.flipY ?? false,
    opacity: metadata?.opacity ?? 1,
    name: metadata?.name,
    mimeType: metadata?.mimeType ?? mimeTypeFromDataUrl(source),
    assetId: metadata?.assetId,
    adjustments: adjustments ?? metadata?.adjustments ?? defaultImageAdjustments(),
  });
}

export function imagePaintFromPatternElement(
  pattern: Element,
  bounds: Rect,
  imageElement: Element | undefined,
  useElement: Element | undefined,
  issues: SvgImportIssueCollector,
): ImagePaint | undefined {
  const metadata = parseCurvaImagePaintAttribute(pattern.getAttribute("data-curva-image-paint"), issues);
  const resolvedImage = imageElement ?? childElements(pattern).find(child => elementLocalName(child) === "image");
  if (!resolvedImage) {
    issues.addOnce("unsupported-image", "Pattern does not contain a resolvable image", elementLocalName(pattern));
    return undefined;
  }

  let paint = imagePaintFromImageElement(resolvedImage, issues);
  if (!paint) {
    return undefined;
  }

  if (metadata) {
    paint = normalizeImagePaint({
      ...paint,
      ...metadata,
      source: paint.source,
      intrinsicWidth: metadata.intrinsicWidth ?? paint.intrinsicWidth,
      intrinsicHeight: metadata.intrinsicHeight ?? paint.intrinsicHeight,
    });
    return paint;
  }

  if (useElement) {
    paint = applyPatternUseToImagePaint(paint, bounds, useElement, issues);
  }

  return paint;
}

function applyPatternUseToImagePaint(
  paint: ImagePaint,
  bounds: Rect,
  useElement: Element,
  _issues: SvgImportIssueCollector,
): ImagePaint {
  const matrix = parsePatternUseMatrix(useElement.getAttribute("transform"));
  if (!matrix) {
    return paint;
  }

  const opacity = readSvgLength(useElement.getAttribute("opacity"), paint.opacity);
  const imageRect = imageRectFromPatternMatrix(bounds, paint.intrinsicWidth, paint.intrinsicHeight, matrix);
  return normalizeImagePaint({
    ...paint,
    fillMode: "crop",
    crop: rectToNormalizedCrop(bounds, imageRect),
    flipX: matrix.a < 0 ? true : paint.flipX,
    flipY: matrix.d < 0 ? true : paint.flipY,
    opacity,
  });
}

function parsePatternUseMatrix(raw: string | null): SvgTransformMatrix | undefined {
  if (!raw?.trim()) {
    return undefined;
  }

  const matrixMatch = raw.trim().match(/^matrix\(([^)]+)\)$/i);
  if (!matrixMatch) {
    return undefined;
  }

  const values = matrixMatch[1]!
    .split(/[\s,]+/)
    .map(value => Number.parseFloat(value))
    .filter(value => Number.isFinite(value));

  if (values.length < 6) {
    return undefined;
  }

  return {
    a: values[0]!,
    b: values[1] ?? 0,
    c: values[2] ?? 0,
    d: values[3]!,
    e: values[4] ?? 0,
    f: values[5] ?? 0,
  };
}

function imageRectFromPatternMatrix(
  bounds: Rect,
  intrinsicWidth: number,
  intrinsicHeight: number,
  matrix: SvgTransformMatrix,
): Rect {
  const width = Math.max(0.0001, Math.abs(bounds.width));
  const height = Math.max(0.0001, Math.abs(bounds.height));
  const absScaleX = Math.abs(matrix.a);
  const absScaleY = Math.abs(matrix.d);
  const imageWidth = absScaleX * width * Math.max(1, intrinsicWidth);
  const imageHeight = absScaleY * height * Math.max(1, intrinsicHeight);
  let translateX = matrix.e;
  let translateY = matrix.f;

  if (matrix.a < 0) {
    translateX -= imageWidth / width;
  }
  if (matrix.d < 0) {
    translateY -= imageHeight / height;
  }

  return {
    x: bounds.x + translateX * width,
    y: bounds.y + translateY * height,
    width: imageWidth,
    height: imageHeight,
  };
}

function rectToNormalizedCrop(bounds: Rect, rect: Rect): ImagePaint["crop"] {
  return {
    x: bounds.width === 0 ? 0 : (rect.x - bounds.x) / bounds.width,
    y: bounds.height === 0 ? 0 : (rect.y - bounds.y) / bounds.height,
    width: bounds.width === 0 ? 1 : rect.width / bounds.width,
    height: bounds.height === 0 ? 1 : rect.height / bounds.height,
  };
}

function mimeTypeFromDataUrl(source: string): string | undefined {
  const match = source.match(/^data:([^;,]+)[;,]/i);
  return match?.[1];
}
